// Topbar bell icon with red-dot indicator + dropdown listing the
// active alerts. Replaces the older "X low stock" + "Y awaiting payment"
// pills in the topbar; consolidates Today's Low Inventory + Awaiting
// Payment panels into a single popover.
//
// Pattern lifted from Utility Desk's PersonaAvatar (mousedown click-outside +
// Escape to close) — see /home/claude/claude_code/projects/UtilityDesk/src/components/TopBar.tsx:183-267.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  ChevronDown,
  Clock,
  MapPin,
} from 'lucide-react';
import type {
  DashboardLowInventoryRow,
  DashboardAgingRow,
} from '@gutterinv/shared';
import { cn } from '../lib/utils.js';
import { useQuoteFormat } from '../lib/useQuoteFormat.js';

interface Props {
  lowInventory: DashboardLowInventoryRow[];
  /** Real total — `lowInventory` is capped to the top-N by deficit for egress. */
  lowInventoryTotal: number;
  awaitingPayments: DashboardAgingRow[];
}

export function NotificationsBell({ lowInventory, lowInventoryTotal, awaitingPayments }: Props) {
  const [open, setOpen] = useState(false);
  // Both sections start collapsed — the bell dropdown is for at-a-glance
  // counts; office expands the section they actually want to drill into.
  const [lowOpen, setLowOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside closes the dropdown.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Escape closes (only attach when open).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const totalAlerts = lowInventoryTotal + awaitingPayments.length;
  const hasAlerts = totalAlerts > 0;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={hasAlerts ? `${totalAlerts} alert${totalAlerts === 1 ? '' : 's'}` : 'No alerts'}
        className={cn(
          'relative flex h-8 w-8 items-center justify-center rounded-md transition-colors',
          open
            ? 'bg-white/20 text-white'
            : 'text-white/70 hover:bg-white/15 hover:text-white',
        )}
      >
        <Bell className="h-4 w-4" />
        {hasAlerts && (
          <span className="absolute right-1.5 top-1.5 flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-destructive" />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-[360px] overflow-hidden rounded-md border border-border bg-card text-foreground shadow-xl">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="eyebrow text-[9px]">Alerts</span>
            <span className="text-xs text-taupe">
              {hasAlerts ? `${totalAlerts} active` : 'All clear'}
            </span>
          </div>

          {!hasAlerts ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckCircle2 className="mb-2 h-8 w-8 text-muted-foreground/40" />
              <div className="text-sm font-medium text-muted-foreground">
                No alerts
              </div>
              <div className="text-xs text-taupe">
                Stock is healthy and every invoice is paid.
              </div>
            </div>
          ) : (
            <div className="max-h-[28rem] overflow-y-auto">
              {/* Low Inventory section */}
              <Section
                title="Low Inventory"
                icon={AlertTriangle}
                iconClass="text-warning"
                count={lowInventoryTotal}
                expanded={lowOpen}
                onToggle={() => setLowOpen((v) => !v)}
              >
                {lowInventory.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-taupe">All stocked.</div>
                ) : (
                  <div className="divide-y divide-border">
                    {lowInventory.map((row) => (
                      <LowInventoryRow key={row.skuId} row={row} onSelect={() => setOpen(false)} />
                    ))}
                    {lowInventoryTotal > lowInventory.length && (
                      <div className="px-3 py-2 text-[0.6875rem] text-taupe">
                        Showing top {lowInventory.length} of {lowInventoryTotal} — see the
                        Inventory page for the full list.
                      </div>
                    )}
                  </div>
                )}
              </Section>

              {/* Awaiting Payment section */}
              <Section
                title="Awaiting Payment"
                icon={Clock}
                iconClass="text-muted-foreground"
                count={awaitingPayments.length}
                expanded={payOpen}
                onToggle={() => setPayOpen((v) => !v)}
              >
                {awaitingPayments.length === 0 ? (
                  <div className="px-3 py-3 text-xs text-taupe">All invoices paid.</div>
                ) : (
                  <div className="divide-y divide-border">
                    {awaitingPayments.map((row) => (
                      <AwaitingPaymentRow key={row.jobId} row={row} onSelect={() => setOpen(false)} />
                    ))}
                  </div>
                )}
              </Section>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  iconClass,
  count,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  icon: typeof Bell;
  iconClass: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 bg-secondary/30 px-3 py-2 text-left transition-colors hover:bg-secondary/60"
      >
        <Icon className={cn('h-4 w-4 shrink-0', iconClass)} />
        <span className="text-[0.8125rem] font-semibold text-foreground">{title}</span>
        <span className="tnum text-xs text-taupe">({count})</span>
        <ChevronDown
          className={cn(
            'ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
            expanded ? '' : '-rotate-90',
          )}
        />
      </button>
      {expanded && children}
    </div>
  );
}

function LowInventoryRow({
  row,
  onSelect,
}: {
  row: DashboardLowInventoryRow;
  onSelect: () => void;
}) {
  return (
    <Link
      to={`/inventory?detail=${row.skuId}`}
      onClick={onSelect}
      className="flex items-start justify-between gap-3 px-3 py-2.5 transition-colors hover:bg-primary/[0.06] no-underline"
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-foreground">
          {row.name}
          {row.variant && (
            <span className="font-normal text-muted-foreground"> · {row.variant}</span>
          )}
        </div>
        <div className="mt-0.5">
          <span className="ticker">{row.code}</span>
        </div>
      </div>
      <div className="text-right whitespace-nowrap">
        <div className="eyebrow text-[8px]">Free</div>
        <div
          className={cn(
            'tnum text-base font-bold leading-tight',
            row.effectivelyFree < 0 ? 'text-destructive' : 'text-warning',
          )}
        >
          {row.effectivelyFree.toLocaleString()}
        </div>
        <div className="text-[0.625rem] uppercase tracking-wider text-taupe">
          {row.unit}
        </div>
      </div>
    </Link>
  );
}

function AwaitingPaymentRow({
  row,
  onSelect,
}: {
  row: DashboardAgingRow;
  onSelect: () => void;
}) {
  const { formatTotal } = useQuoteFormat();
  return (
    <Link
      to={`/jobs/${row.jobId}`}
      onClick={onSelect}
      className="flex items-start justify-between gap-3 px-3 py-2.5 transition-colors hover:bg-primary/[0.06] no-underline"
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-foreground">
          {row.displayName}
        </div>
        <div className="text-xs text-muted-foreground">{row.customerName}</div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          {row.quoteAmountCents !== null && (
            <span className="tnum">{formatTotal(row.quoteAmountCents)}</span>
          )}
          {row.address && (
            <>
              {row.quoteAmountCents !== null && <span>·</span>}
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{row.address}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
        <Clock className="h-3 w-3" />
        {row.daysInStage}d
      </div>
    </Link>
  );
}
